import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { getErrorMessage } from './error';
import { LanguagesAtom, PageTitleAtom, SelectedLanguageAtom } from '@projtech-package/react-components/lib';
import { IPrayerTimes } from '@dummy/config';
import { useParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';
// import { EventRecurrenceDtoDaysOfWeekEnum, PrayerTimeDto } from '@api';
import { IPrayerTimesProps } from '@components/prayer';
import { INotificationTypeEnum } from '@cake-api/interface/public/home';
import { ISubscriber } from '@cake-api/interface/teacher';
import { TicketStatus } from '@cake-api/interface/public/ticket';
import { quran } from '@data/ayahs';
import { surah } from '@data/quran';
import { FormikErrors, FormikTouched } from 'formik';
import { SubscriberStatusEnum } from '@cake-api/interface/student';
import { IProfile } from '@cake-api/interface/auth/profile';
import { SnackbarAtom } from '@store';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type Day = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';

const dayMap: Record<Day, number> = {
	Sun: 0,
	Mon: 1,
	Tue: 2,
	Wed: 3,
	Thu: 4,
	Fri: 5,
	Sat: 6,
};

function getStartOfWeek(date: Dayjs) {
	// date.day() is always Sunday=0..Saturday=6 regardless of the active dayjs
	// locale, unlike startOf('week') which shifts with the locale's week start
	// (e.g. Saturday for Arabic) and would break the Sun-based offsets in dayMap.
	return date.subtract(date.day(), 'day').startOf('day');
}

export function getWeekDates(selectedDate: Dayjs, fromDate: Dayjs, toDate: Dayjs, day: Day): string[] {
	const targetDay = dayMap[day];

	const selectedWeek = getStartOfWeek(selectedDate);

	const weeks: string[] = [];

	// Woche davor
	const previousWeekDate = selectedWeek.subtract(1, 'week').add(targetDay, 'day');

	if (previousWeekDate.isSameOrAfter(fromDate, 'day') && previousWeekDate.isSameOrBefore(toDate, 'day')) {
		weeks.push(previousWeekDate.format('YYYY-MM-DD'));
	}

	// Aktuelle / ausgewählte Woche
	const currentWeekDate = selectedWeek.add(targetDay, 'day');

	if (currentWeekDate.isSameOrAfter(fromDate, 'day') && currentWeekDate.isSameOrBefore(toDate, 'day')) {
		weeks.push(currentWeekDate.format('YYYY-MM-DD'));
	}

	// Woche danach
	const nextWeekDate = selectedWeek.add(1, 'week').add(targetDay, 'day');

	if (nextWeekDate.isSameOrAfter(fromDate, 'day') && nextWeekDate.isSameOrBefore(toDate, 'day')) {
		weeks.push(nextWeekDate.format('YYYY-MM-DD'));
	}

	return weeks;
}

export const useHandleError = () => {
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [_, setSnackbar] = useAtom(SnackbarAtom);

	const getMessage = (err: unknown | undefined) => {
		if (err) {
			console.log(err);

			setErrorMessage(getErrorMessage(err));
			setSnackbar({
				color: 'error',
				text: getErrorMessage(err) || 'An error occurred',
			});
		} else {
			setErrorMessage(undefined);
		}
	};

	return { errorMessage, getMessage };
};

export const getCurrentPrayer = (prayerTimes: IPrayerTimesProps) => {
	const now = new Date();
	const currentTime = now.getHours() * 60 + now.getMinutes(); // Zeit in Minuten seit Mitternacht

	// Gebetszeiten in Minuten seit Mitternacht
	const timesInMinutes = Object.entries(prayerTimes).map(([name, time]) => {
		const [hours, minutes] = (time as string)?.split(':').map(Number);
		return { name, timeInMinutes: hours * 60 + minutes };
	});

	// Gebetszeiten sortieren (falls nicht in Reihenfolge)
	timesInMinutes.sort((a, b) => a.timeInMinutes - b.timeInMinutes);

	// Suche das nächste Gebet
	for (let i = 0; i < timesInMinutes.length; i++) {
		const currentPrayer = timesInMinutes[i];
		const nextPrayer = timesInMinutes[(i + 1) % timesInMinutes.length]; // Zyklisch zu Fajr wechseln

		if (currentTime < currentPrayer.timeInMinutes) {
			return currentPrayer.name; // Zeige das nächste Gebet
		}

		// Wenn die aktuelle Zeit zwischen zwei Gebetszeiten liegt
		if (currentTime >= currentPrayer.timeInMinutes && currentTime < nextPrayer.timeInMinutes) {
			return nextPrayer.name; // Zeige das nächste Gebet
		}
	}

	// Wenn die aktuelle Zeit nach Isha, gehört sie zu Fajr
	return 'Fajr';
};

export const useRenderTime = (time: string) => {
	const [distance, setDistance] = useState(0); // Verbleibende Zeit in Millisekunden

	useEffect(() => {
		const targetTime = new Date(time).getTime();

		const updateDistance = () => {
			const now = new Date().getTime();
			const diff = targetTime - now;

			if (diff <= 0) {
				setDistance(0); // Zeit abgelaufen
				return;
			}

			setDistance(diff); // Verbleibende Zeit in Millisekunden setzen
		};

		// Initiale Berechnung
		updateDistance();

		// Interval für die Aktualisierung (jede Minute)
		const interval = setInterval(updateDistance, 60000); // 60000ms = 1 Minute
		return () => clearInterval(interval); // Aufräumen bei unmount
	}, [time]);

	// Minuten, Stunden und Tage aus `distance` ableiten
	const min = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
	const hour = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
	const day = Math.floor(distance / (1000 * 60 * 60 * 24));

	return { min, hour, day };
};

export const useGetParamId = <T extends boolean>(key: string, isNumber: T): T extends true ? number : string => {
	const paramId = useParams();
	const id = paramId[key];
	const isValidInteger = !!id && Number.isInteger(parseInt(id, 10));

	return isNumber
		? ((isValidInteger ? parseInt(id, 10) : undefined) as T extends true ? number : string)
		: (id as T extends true ? number : string);
};

export const getDummyApi = async <T>(
	data: T,
	length: number,
): Promise<AxiosResponse<{ data: T; count: number }, any>> => {
	return Promise.resolve({
		data: {
			data: data,
			count: length,
		},
		status: 200,
		statusText: 'OK',
	}) as Promise<AxiosResponse<{ data: T; count: number }, any>>;
};

export const postDummyApi = async <T>(data: T): Promise<AxiosResponse<any>> => {
	return Promise.resolve({
		data: {
			data: {} as object,
			message: 'Success',
			status: 'success',
		},
		status: 200,
		statusText: 'OK',
	}) as unknown as Promise<AxiosResponse<any>>;
};

export const useIsRTL = () => {
	const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur']; // Liste der RTL-Sprachcodes

	const lang = useAtomValue(SelectedLanguageAtom);

	return RTL_LANGUAGES.includes(lang);
};

export const getHijriMonth = (month: number) => {
	switch (month) {
		case 1:
			return 'Muharram';
		case 2:
			return 'Safar';
		case 3:
			return 'Rabi al-awwal';
		case 4:
			return 'Rabi al-thani';
		case 5:
			return 'Jumada al-awwal';
		case 6:
			return 'Jumada al-thani';
		case 7:
			return 'Rajab';
		case 8:
			return "Sha'ban";
		case 9:
			return 'Ramadan';
		case 10:
			return 'Shawwal';
		case 11:
			return "Dhu al-Qi'dah";
		case 12:
			return 'Dhu al-Hijjah';
		default:
			return '';
	}
};

export const isValidUrl = (urlString: string): boolean => {
	const urlPattern = new RegExp('^(https?:\\/\\/)?');

	return !!urlPattern.test(urlString);
};

export const getImgUrl = (img?: string) => {
	if (!img) return '/assets/header.png';

	if (img.startsWith('http')) return img;

	return import.meta.env.VITE_CAKE_URL + '/' + img;
};

export const getYouTubeEmbedUrl = (url: string) => {
	if (url.includes('embed')) return url;

	if (url.includes('youtube.com')) {
		const videoId = new URL(url).searchParams.get('v');
		return `https://www.youtube.com/embed/${videoId}`;
	}

	return url;
};

export function isTextRightToLeft(text: string) {
	const isTextRightToLeft = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text[0]);

	return isTextRightToLeft;
}

export const useIsHome = () => {
	const activeTab = useAtomValue(PageTitleAtom);
	const isHome = activeTab === 'Home';
};

export const renderDate = (date: any) => {
	switch (date) {
		case 'SUN':
			return 'Sonntag';
		case 'MON':
			return 'Montag';
		case 'TUE':
			return 'Dienstag';
		case 'WED':
			return 'Mittwoch';
		case 'THU':
			return 'Donnerstag';
		case 'FRI':
			return 'Freitag';
		case 'SAT':
			return 'Samstag';
		default:
			return '';
	}
};

export const renderLongText = (text: string, length: number) => {
	if (text.length > length) {
		return text.slice(0, length) + '...';
	}
	return text;
};

export function toArabicNumber(numberString: string | number): string {
	const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

	return numberString
		?.toString()
		?.split('')
		?.map((char) => (/\d/.test(char) ? arabicDigits[parseInt(char)] : char))
		?.join('');
}

type WordEntry = {
	text: string;
	verseId: number;
	numberInSurah: number;
	wordIndex: number;
	verseText: string;
	suraName: string;
};

export function shouldWrapLine(
	currentLine: WordEntry[],
	newWord: WordEntry,
	fontSize: number,
	container: HTMLElement,
	padding: number = 50,
	fontFamily: string = 'Amiri, serif',
): boolean {
	const testSpan = document.createElement('span');
	testSpan.style.visibility = 'hidden';
	testSpan.style.position = 'absolute';
	testSpan.style.fontSize = `${fontSize}px`;
	testSpan.style.fontFamily = fontFamily;
	testSpan.style.direction = 'rtl';

	const testText = [...currentLine.map((w) => w.text), newWord.text].join(' ');
	testSpan.innerText = testText;

	document.body.appendChild(testSpan);
	const exceedsWidth = testSpan.offsetWidth + padding > container.clientWidth - 5;
	document.body.removeChild(testSpan);

	return exceedsWidth && currentLine.length > 0;
}

export const getPlanColors = (index = 0, subIndex = 0) => {
	const colors = [
		['#FFF3DA', '#F87A1A', '#FFD0B5'],
		['#DAF5E6', '#018B07', '#008B076B'],
		['#FFDADA', '#F2322C', '#FF514C3D'],
	];

	if (index < 0 || index >= colors.length) {
		index = 0; // Fallback to the first color if index is out of bounds
	}
	if (subIndex < 0 || subIndex >= colors[index].length) {
		subIndex = 0; // Fallback to the first sub-color if subIndex is out of bounds
	}
	return colors[index][subIndex] || '#FFF'; // Default to white if no color found
};

export const getRandomColors = (index = 0, subIndex = 0) => {
	const colors = [
		[
			'rgba(255, 227, 190, 1)',
			'rgba(255, 227, 190, 1)',
			'conic-gradient(from -3.29deg at 100% -13%, #FFA502 0deg, #FF6348 360deg)',
		],
		['rgba(198, 255, 191, 1)', 'rgba(198, 255, 191, 1)', 'linear-gradient(275.76deg, #2ED573 44.33%, #7BED9F 98.56%)'],
		['rgba(197, 209, 250, 1)', 'rgba(197, 209, 250, 1)', 'linear-gradient(90.88deg, #5E81F2 0.59%, #7F9CFF 99.41%)'],
		[
			'rgba(255, 208, 233, 1)',
			'rgba(255, 208, 233, 1)',
			'conic-gradient(from -3.29deg at 100% -13%, #FF99DF 0deg, #FF4870 360deg)',
		],
		[
			'rgba(255, 244, 190, 1)',
			'rgba(255, 244, 190, 1)',
			'conic-gradient(from -3.29deg at 100% -13%, #FFE4BE 0deg, #FFBE0C 360deg)',
		],
		['rgba(190, 255, 251, 1)', 'rgba(190, 255, 251, 1)', 'linear-gradient(180deg, #17C3B2 0%, #99F3EB 100%)'],
		[
			'rgba(252, 190, 255, 1)',
			'rgba(252, 190, 255, 1)',
			'conic-gradient(from 0deg at 50% 50%, #E94057 0deg, #F27121 360deg)',
		],
		['rgba(223, 190, 255, 1)', 'rgba(223, 190, 255, 1)', 'linear-gradient(90deg, #9D50BB 0%, #6E48AA 100%)'],
		[
			'rgba(255, 219, 190, 1)',
			'rgba(255, 219, 190, 1)',
			'linear-gradient(45deg, #FF9A8B 0%, #FF6A88 50%, #FF99AC 100%)',
		],
		['rgba(190, 255, 208, 1)', 'rgba(190, 255, 208, 1)', 'linear-gradient(120deg, #A1FFCE 0%, #FAFFD1 100%)'],
	];

	if (index < 0 || index >= colors.length) {
		index = 0; // Fallback to the first color if index is out of bounds
	}
	if (subIndex < 0 || subIndex >= colors[index].length) {
		subIndex = 0; // Fallback to the first sub-color if subIndex is out of bounds
	}

	return colors[index][subIndex];
};

export const WEEK_DAYS = {
	MON: 'Montag',
	TUE: 'Dienstag',
	WED: 'Mittwoch',
	THU: 'Donnerstag',
	FRI: 'Freitag',
	SAT: 'Samstag',
	SUN: 'Sonntag',
};

export const replaceDay = (text: string) => {
	return text
		.replace(/MON/g, WEEK_DAYS.MON)
		.replace(/TUE/g, WEEK_DAYS.TUE)
		.replace(/WED/g, WEEK_DAYS.WED)
		.replace(/THU/g, WEEK_DAYS.THU)
		.replace(/FRI/g, WEEK_DAYS.FRI)
		.replace(/SAT/g, WEEK_DAYS.SAT)
		.replace(/SUN/g, WEEK_DAYS.SUN);
};

export const MONTHS = {
	JAN: 'Januar',
	FEB: 'Februar',
	MAR: 'März',
	APR: 'April',
	MAY: 'Mai',
	JUN: 'Juni',
	JUL: 'Juli',
	AUG: 'August',
	SEP: 'September',
	OCT: 'Oktober',
	NOV: 'November',
	DEC: 'Dezember',
};

export const replaceMonth = (text: string) => {
	return text
		.replace(/JAN/g, MONTHS.JAN)
		.replace(/FEB/g, MONTHS.FEB)
		.replace(/MAR/g, MONTHS.MAR)
		.replace(/APR/g, MONTHS.APR)
		.replace(/MAY/g, MONTHS.MAY)
		.replace(/JUN/g, MONTHS.JUN)
		.replace(/JUL/g, MONTHS.JUL)
		.replace(/AUG/g, MONTHS.AUG)
		.replace(/SEP/g, MONTHS.SEP)
		.replace(/OCT/g, MONTHS.OCT)
		.replace(/NOV/g, MONTHS.NOV)
		.replace(/DEC/g, MONTHS.DEC);
};

export const getJuzLabel = (juz: number): string => {
	const parts = [
		'الجزء الأول',
		'الجزء الثاني',
		'الجزء الثالث',
		'الجزء الرابع',
		'الجزء الخامس',
		'الجزء السادس',
		'الجزء السابع',
		'الجزء الثامن',
		'الجزء التاسع',
		'الجزء العاشر',
		'الجزء الحادي عشر',
		'الجزء الثاني عشر',
		'الجزء الثالث عشر',
		'الجزء الرابع عشر',
		'الجزء الخامس عشر',
		'الجزء السادس عشر',
		'الجزء السابع عشر',
		'الجزء الثامن عشر',
		'الجزء التاسع عشر',
		'الجزء العشرون',
		'الجزء الحادي والعشرون',
		'الجزء الثاني والعشرون',
		'الجزء الثالث والعشرون',
		'الجزء الرابع والعشرون',
		'الجزء الخامس والعشرون',
		'الجزء السادس والعشرون',
		'الجزء السابع والعشرون',
		'الجزء الثامن والعشرون',
		'الجزء التاسع والعشرون',
		'الجزء الثلاثون',
	];

	if (juz < 1 || juz > 30) return 'غير معروف';
	return parts[juz - 1];
};

export function stripTimezoneOffset(dateStr?: string | null): string {
	// Regex sucht + oder - gefolgt von 2 Ziffern, Doppelpunkt, 2 Ziffern am Ende
	return dateStr?.replace(/([+-]\d{2}:\d{2})$/, '') || '';
}

export const markValues = [
	{
		value: 0,
		label: '0 - Nicht bestanden',
	},
	{
		value: 10,
		label: '1 - Wiederholen',
	},
	{
		value: 20,
		label: '2 - Wiederholen',
	},
	{
		value: 30,
		label: '3 - Mangelhaft',
	},
	{
		value: 40,
		label: '4 - Mangelhaft',
	},
	{
		value: 50,
		label: '5 - Ausreichend',
	},
	{
		value: 60,
		label: '6 - Mittel',
	},
	{
		value: 70,
		label: '7 - Gut',
	},
	{
		value: 80,
		label: '8 - Sehr Gut',
	},
	{
		value: 90,
		label: '9 - Super',
	},
	{ value: 100, label: '10 - Super' },
];

// utils/score.ts

/** clamp helper */
const clamp = (min: number, max: number, v: number) => Math.min(max, Math.max(min, v));

/** 1) Punkte -> Prozent (0–100) */
export function scoreToPercent(score: number, maxScore: number, decimals = 0): number {
	if (maxScore <= 0) return 0;
	const pct = (clamp(0, maxScore, score) / maxScore) * 100;
	const factor = 10 ** decimals;
	return Math.round(pct * factor) / factor;
}

/** 2) Prozent -> Bewertung (0–10)
 * 90–100 => 10, 80–89 => 9, 70–79 => 8, ... , 0–9 => 0
 */
export function percentageToRating(pct: number): number {
	const p = clamp(0, 100, Math.floor(pct)); // ganzzahlig & begrenzt
	if (p >= 99) return 10;
	if (p >= 80) return 9;
	if (p >= 70) return 8;
	if (p >= 60) return 7;
	if (p >= 50) return 6;
	if (p >= 40) return 5;
	if (p >= 30) return 4;
	if (p >= 20) return 3;
	if (p >= 10) return 2;
	if (p > 0) return 1;
	return 0;
}
export function percentageToPercentageRating(pct: number): number {
	const p = clamp(0, 100, Math.floor(pct)); // ganzzahlig & begrenzt
	if (p >= 99) return 100;
	if (p >= 80) return 90;
	if (p >= 70) return 80;
	if (p >= 60) return 70;
	if (p >= 50) return 60;
	if (p >= 40) return 50;
	if (p >= 30) return 40;
	if (p >= 20) return 30;
	if (p >= 10) return 20;
	if (p > 0) return 10;
	return 0;
}

// mark is 0-10, returns 0–3
export function markToPercentage(mark: number): number {
	if (mark > 7) return 3;
	if (mark > 6) return 2;
	if (mark > 4) return 1;
	return 0;
}

/** 3) Direkt von Punkten -> Bewertung */
export function ratingFromScore(score: number, maxScore: number): number {
	const pct = scoreToPercent(score, maxScore);
	return percentageToRating(pct);
}

// utils/score.ts

/** Prozent -> Punkte auf maxScore.
 *  Beispiel: 87.5%, max 40  => 35.0
 *            350%,  max 40  => 140  (wenn clampTo100=false)
 */
export function percentToScore(
	percent: number,
	maxScore: number,
	opts: { decimals?: number; clampTo100?: boolean } = {},
): number {
	const { decimals = 1, clampTo100 = true } = opts;
	const p = clampTo100 ? Math.min(100, Math.max(0, percent)) : percent;
	const raw = (p / 100) * Math.max(0, maxScore);
	const f = 10 ** decimals;
	return Math.round(raw * f) / f;
}

export const getNotificationType = (type: INotificationTypeEnum, t: any, subscriber?: ISubscriber) => {
	switch (type) {
		case INotificationTypeEnum.CONTENT:
			return (
				t('Inhalt wurde hinzugefügt zu') + (subscriber ? ` ${subscriber.name || ''} ${subscriber.lname || ''}` : '')
			);
		case INotificationTypeEnum.HOMEWORK:
			return (
				t('Hausaufgaben wurden vergeben in') + (subscriber ? ` ${subscriber.name || ''} ${subscriber.lname || ''}` : '')
			);
		case INotificationTypeEnum.NOTE:
			return t('Hinweis hinzugefügt für') + (subscriber ? ` ${subscriber.name || ''} ${subscriber.lname || ''}` : '');
		case INotificationTypeEnum.TEST:
			return t('Test wurde hinzugefügt zu') + (subscriber ? ` ${subscriber.name || ''} ${subscriber.lname || ''}` : '');
		default:
			return 'Unbekannter Typ';
	}
};

export const normDigits = (s?: string | number) => {
	if (typeof s === 'number') s = String(s);
	if (!s) return '';

	const ar = '٠١٢٣٤٥٦٧٨٩';
	const fa = '۰۱۲۳۴۵۶۷۸۹';
	const map: Record<string, string> = {};
	for (let i = 0; i < 10; i++) {
		map[ar[i]] = String(i);
		map[fa[i]] = String(i);
	}
	return s.replace(/[٠-٩۰-۹]/g, (ch) => map[ch] ?? ch ?? '');
};

export function resizeImage(file: File, scale: number, setImage: (image: string) => void): Promise<File> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width * scale;
			canvas.height = img.height * scale;

			const ctx = canvas.getContext('2d');
			if (!ctx) return reject('Canvas not supported');

			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

			canvas.toBlob(
				(blob) => {
					if (blob) {
						const resizedFile = new File([blob], file.name, { type: file.type });
						resolve(resizedFile);
					} else {
						reject('Fehler beim Konvertieren');
					}
				},
				file.type,
				0.8, // Qualität (nur für JPEG/WEBP)
			);
		};
		img.onerror = reject;

		console.log('File:', file, URL.createObjectURL(file));
		setImage(URL.createObjectURL(file));
		img.src = URL.createObjectURL(file);
	});
}

export const isDev = () => {
	const hostname = window.location.hostname;
	return false;
	return import.meta.env.VITE_IS_DEV === 'true';
	return hostname === 'localhost' || hostname === '127.0.0.1' || import.meta.env.VITE_IS_DEV === 'true';

	// return !hostname.startsWith('https://korani.eu');
};

export const renderTicketStatus = (status: TicketStatus) => {
	switch (status) {
		case TicketStatus.OPEN:
			return 'Offen';
		case TicketStatus.IN_PROGRESS:
			return 'In Bearbeitung';
		case TicketStatus.CLOSED:
			return 'Erledigt';
		default:
			return 'Unbekannt';
	}
};

export const renderUserStatus = (status: IProfile['type']) => {
	switch (status) {
		case 'ADMIN':
			return 'Admin';
		case 'STUDENT':
			return 'Schüler';
		case 'TEACHER':
			return 'Lehrer';
		default:
			return 'Unbekannt';
	}
};

export const getSurahByVerseId = (verseId: number) => {
	return surah.find((s) => s.firstVerseId <= verseId && s.lastVerseId >= verseId) || null;
};

export const getVerse = (verseId: number) => {
	if (verseId === 0) return null;

	const selectedVerse = quran?.find((v) => Number(v.id) === verseId);

	if (!selectedVerse) return null;

	return {
		verse: selectedVerse,
		sura: surah.find((s) => s.id === Number(selectedVerse?.surah_id)),
	};
};

export const checkFromikErrors = (
	fieldsToValidate: string[],
	setTouched: (touched: FormikTouched<any>, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>,
	validateField?: (name: string) => Promise<void> | Promise<string | undefined>,
	errors?: FormikErrors<any>,
) => {
	return new Promise<void>(async (resolve, reject) => {
		try {
			// setTouched mit shouldValidate: true gibt die aktuellen Errors zurück
			const errors = await setTouched(
				Object.fromEntries(fieldsToValidate.map((key) => [key, true])),
				true, // ✅ triggert Validierung und gibt frische Errors zurück
			);

			const hasErrors = fieldsToValidate.some((field) => errors && errors[field]);

			if (hasErrors) {
				reject();
			} else {
				resolve();
			}
		} catch (error) {
			console.error('Fehler beim Validieren:', error);
			reject(error);
		}
	});
	// return new Promise((resolve, reject) => {
	// 	try {
	// 		// ⬇ Nur bestimmte Felder als "berührt" markieren
	// 		setTouched(Object.fromEntries(fieldsToValidate.map((key) => [key, true])));

	// 		// ⬇ Validierung nur für bestimmte Felder ausführen
	// 		Promise.all(fieldsToValidate.map((field) => validateField(field.toString()))).then(() => {
	// 			const validationErrors = fieldsToValidate.filter((field) => errors[field]);

	// 			if (validationErrors.length === 0) {
	// 				resolve(''); // 🟢 Erfolgreich: Promise wird erfüllt
	// 			} else {
	// 				reject();
	// 			}
	// 		});
	// 	} catch (error) {
	// 		console.error('Fehler beim Validieren:', error);
	// 		reject(error);
	// 	}
	// });
};

export const subscriberStatus = (status: number) => {
	switch (status) {
		case 1:
			return SubscriberStatusEnum.DONE;
		case 2:
			return SubscriberStatusEnum.NEW;
		case 3:
			return SubscriberStatusEnum.SELECT_EDUCATION_PATH;
		case 4:
			return SubscriberStatusEnum.IN_PROGRESS;
		case 5:
			return SubscriberStatusEnum.CANCELED;
		case 6:
			return SubscriberStatusEnum.PAYMENT_NOT_COMPLETE_YET;
		case 7:
			return SubscriberStatusEnum.REJECTED;
		case 8:
			return SubscriberStatusEnum.ACCEPTED;
		case 9:
			return SubscriberStatusEnum.REQUEST_CANCEL;
		case 10:
			return SubscriberStatusEnum.RENEW_SUBSCRIPTION;
		case 11:
			return SubscriberStatusEnum.SUBSCRIPTION_RENEWED;
		default:
			return SubscriberStatusEnum.UNKNOWN_STATUS;
	}
};
export const transSubscriberStatus = (status: number) => {
	switch (subscriberStatus(status)) {
		case SubscriberStatusEnum.NEW:
			return 'Neu';
		case SubscriberStatusEnum.DONE:
			return 'Warten auf Zuweisung';
		case SubscriberStatusEnum.RENEW_SUBSCRIPTION:
			return 'Abonnement erneuern';
		case SubscriberStatusEnum.SUBSCRIPTION_RENEWED:
			return 'Erneuert, bitte warten';
		case SubscriberStatusEnum.SELECT_EDUCATION_PATH:
			return 'Bildungsweg wählen';
		case SubscriberStatusEnum.IN_PROGRESS:
			return 'In Bearbeitung';
		case SubscriberStatusEnum.CANCELED:
			return 'Abgebrochen';
		case SubscriberStatusEnum.REJECTED:
			return 'Abgelehnt';
		case SubscriberStatusEnum.ACCEPTED:
			return 'Zugewiesen';
		case SubscriberStatusEnum.REQUEST_CANCEL:
			return 'Stornierung angefragt';
		case SubscriberStatusEnum.PAYMENT_NOT_COMPLETE_YET:
			return 'Zahlung ausstehend';
		default:
			return 'Wählen Sie das passende Abonnement';
	}
};

// Capacitor.getPlatform() only reports 'ios' inside the native app shell — it
// reports 'web' in mobile Safari/Chrome even though the OS is iOS, so callers
// that need to work around WebKit-only rendering bugs (e.g. backdrop-filter
// inside SVG foreignObject) must check the OS itself, not the runtime.
export const isIOSDevice = (): boolean => {
	if (typeof navigator === 'undefined') return false;

	const platform = navigator.platform || '';
	const userAgent = navigator.userAgent || '';

	return (
		/iP(hone|od|ad)/.test(platform) ||
		// iPadOS 13+ reports as 'MacIntel' but exposes touch support, unlike real Macs
		(platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
		/iP(hone|od|ad)/.test(userAgent)
	);
};

export const isIframe = window.self !== window.top || window.location.pathname.startsWith('/iframe');

export const handleIframeUrl = () => {};

const interpolateScale = (value: number, scaleMap: Record<number, number>) => {
	const clampedValue = Math.max(1, Math.min(10, value));
	const lowerBound = Math.floor(clampedValue);
	const upperBound = Math.ceil(clampedValue);

	if (lowerBound === upperBound) {
		return scaleMap[lowerBound] ?? scaleMap[1];
	}

	const lowerScale = scaleMap[lowerBound] ?? scaleMap[1];
	const upperScale = scaleMap[upperBound] ?? scaleMap[10];
	const progress = clampedValue - lowerBound;

	return lowerScale + (upperScale - lowerScale) * progress;
};

export function buildQuranReaderPageLayout(
	options: {
		readerFontSize?: number;
		viewportWidth?: number;
		viewportHeight?: number;
	} = {
		readerFontSize: 16,
		viewportWidth: window.innerWidth,
		viewportHeight: window.innerHeight,
	},
) {
	const safeWidth = Math.max(options?.viewportWidth ?? window.innerWidth, 280);
	const safeHeight = Math.max(options?.viewportHeight ?? window.innerHeight, 0);
	const normalLineCount = 15;
	const surahStartLineCount = 13;
	const surahFrameHeightPx = 40;
	const readerHeaderHeightPx = 18;
	const readerFooterHeightPx = 18;
	const readerChromeHeightPx = readerHeaderHeightPx + readerFooterHeightPx;
	const quranScale = Math.min(10, Math.max(1, ((options?.readerFontSize || 0) - 10) / 2));
	const scaleMap: Record<number, number> = {
		1: 0.052,
		2: 0.064,
		3: 0.078,
		4: 0.09,
		5: 0.102,
		6: 0.114,
		7: 0.126,
		8: 0.138,
		9: 0.15,
		10: 0.162,
	};
	// const horizontalPaddingPx = safeWidth >= 430 ? 10 : 8;
	const horizontalPaddingPx = safeWidth >= 430 ? 12 : 10; // it was 10 : 8;
	const verticalPaddingPx = safeHeight >= 760 ? 6 : 4;
	const contentWidth = Math.max(safeWidth - horizontalPaddingPx * 2, 240);
	const widthBasedFontSize = contentWidth * interpolateScale(quranScale, scaleMap) * 0.74;
	const bodyHeightPx = Math.max(240, safeHeight - readerChromeHeightPx);
	const minLineHeightRatio = 1.72;
	const maxFontFromHeight = Math.floor((bodyHeightPx - verticalPaddingPx * 2) / (normalLineCount * minLineHeightRatio));
	const desiredFontSizePx = Math.floor(Math.max(17, Math.min(widthBasedFontSize, Math.max(36, maxFontFromHeight))));
	const availableLineHeightPx = (bodyHeightPx - verticalPaddingPx * 2) / normalLineCount;
	const heightBasedLineHeightRatio = availableLineHeightPx / desiredFontSizePx;
	const lineHeightRatio = Number(Math.max(minLineHeightRatio, heightBasedLineHeightRatio).toFixed(3));
	const lineSlotHeightPx = Number((desiredFontSizePx * lineHeightRatio).toFixed(2));
	const pageHeightPx = Math.ceil(
		Math.max(safeHeight, readerChromeHeightPx + verticalPaddingPx * 2 + lineSlotHeightPx * normalLineCount),
	);

	return {
		desiredFontSizePx,
		lineHeightRatio,
		horizontalPaddingPx,
		verticalPaddingPx,
		pageHeightPx,
		bodyHeightPx: Math.max(bodyHeightPx, verticalPaddingPx * 2 + lineSlotHeightPx * normalLineCount),
		lineSlotHeightPx,
		surahFrameHeightPx,
		normalLineCount,
		surahStartLineCount,
		allowLineWrap: (options?.readerFontSize || 16) > 16,
	};
}

export const getSurahName = (id: number) => {
	let number = '0';

	if (id < 10) {
		number = '00' + id;
	} else if (id < 100) {
		number = '0' + id;
	} else {
		number = '' + id;
	}
	return 'surah' + number;
};

export const getSurahNameByPageId = (pageId: number) => {
	const surah = getSurahByVerseId(pageId * 20 + 1);
	if (!surah) return null;

	return surah;
};
